<template>
  <div class="page">
    <h2>H5 虚拟手机号管理</h2>
    <p class="hint">为无国内手机号的海外员工生成虚拟手机号与一次性验证码，用于 H5 首次绑定。</p>

    <div class="toolbar">
      <select v-model="filters.status">
        <option value="">全部状态</option>
        <option value="PENDING">待使用</option>
        <option value="USED">已使用</option>
        <option value="REVOKED">已作废</option>
      </select>
      <button :disabled="loading" @click="loadData">查询</button>
      <button class="primary" :disabled="loading" @click="openCreateDialog">生成凭证</button>
    </div>

    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th>虚拟手机号</th>
          <th>员工备注</th>
          <th>状态</th>
          <th>绑定用户</th>
          <th>创建时间</th>
          <th>过期时间</th>
          <th>使用时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.virtualPhone }}</td>
          <td>{{ item.label || '—' }}</td>
          <td>{{ statusLabel(item.status) }}</td>
          <td>{{ item.boundUserNickname || '—' }}</td>
          <td>{{ formatTime(item.createdAt) }}</td>
          <td>{{ formatTime(item.expiresAt) }}</td>
          <td>{{ formatTime(item.usedAt) }}</td>
          <td>
            <template v-if="item.status === 'PENDING'">
              <button :disabled="loading" @click="regenerate(item)">重新生成验证码</button>
              <button :disabled="loading" @click="revoke(item)">作废</button>
            </template>
            <span v-else>—</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="createDialog" class="dialog-mask">
      <div class="dialog">
        <h3>{{ credentialResult ? '凭证已生成' : '生成虚拟手机号凭证' }}</h3>
        <template v-if="!credentialResult">
          <label class="field-label">员工备注（可选）</label>
          <input v-model="createLabel" type="text" maxlength="100" placeholder="如：John / 海外理疗师" class="input" />
          <div class="actions">
            <button :disabled="loading" @click="closeCreateDialog">取消</button>
            <button class="primary" :disabled="loading" @click="submitCreate">生成</button>
          </div>
        </template>
        <template v-else>
          <p class="warn">以下信息仅展示一次，请立即复制并转交员工。</p>
          <div class="credential-box">
            <div class="credential-row">
              <span class="credential-label">虚拟手机号</span>
              <code>{{ credentialResult.virtualPhone }}</code>
              <button type="button" @click="copyText(credentialResult.virtualPhone)">复制</button>
            </div>
            <div class="credential-row">
              <span class="credential-label">验证码</span>
              <code>{{ credentialResult.verificationCode }}</code>
              <button type="button" @click="copyText(credentialResult.verificationCode)">复制</button>
            </div>
            <div v-if="credentialResult.label" class="credential-row">
              <span class="credential-label">备注</span>
              <span>{{ credentialResult.label }}</span>
            </div>
            <div class="credential-row">
              <span class="credential-label">过期时间</span>
              <span>{{ formatTime(credentialResult.expiresAt) }}</span>
            </div>
          </div>
          <div class="actions">
            <button class="primary" @click="closeCreateDialog">关闭</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { therapyH5VirtualPhoneService } from '@/services/therapyH5VirtualPhoneService'

const STATUS_LABELS = {
  PENDING: '待使用',
  USED: '已使用',
  REVOKED: '已作废'
}

export default {
  name: 'TherapyH5VirtualPhoneManagement',
  data() {
    return {
      list: [],
      filters: { status: '' },
      loading: false,
      createDialog: false,
      createLabel: '',
      credentialResult: null
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    statusLabel(status) {
      return STATUS_LABELS[status] || status
    },
    formatTime(value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').slice(0, 19)
    },
    async loadData() {
      this.loading = true
      try {
        const params = { page: 0, size: 100 }
        if (this.filters.status) params.status = this.filters.status
        const res = await therapyH5VirtualPhoneService.fetchList(params)
        this.list = res.data?.content || []
      } catch (e) {
        alert(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.createLabel = ''
      this.credentialResult = null
      this.createDialog = true
    },
    closeCreateDialog() {
      this.createDialog = false
      this.credentialResult = null
      this.createLabel = ''
      this.loadData()
    },
    async submitCreate() {
      this.loading = true
      try {
        const res = await therapyH5VirtualPhoneService.create(this.createLabel.trim() || undefined)
        this.credentialResult = res.data
        await this.loadData()
      } catch (e) {
        alert(e.message || '生成失败')
      } finally {
        this.loading = false
      }
    },
    async regenerate(item) {
      if (!confirm(`确定为 ${item.virtualPhone} 重新生成验证码？旧验证码将立即失效。`)) return
      this.loading = true
      try {
        const res = await therapyH5VirtualPhoneService.regenerateCode(item.id)
        this.credentialResult = res.data
        this.createDialog = true
        await this.loadData()
      } catch (e) {
        alert(e.message || '重新生成失败')
      } finally {
        this.loading = false
      }
    },
    async revoke(item) {
      if (!confirm(`确定作废 ${item.virtualPhone} 的凭证？`)) return
      this.loading = true
      try {
        await therapyH5VirtualPhoneService.revoke(item.id)
        await this.loadData()
      } catch (e) {
        alert(e.message || '作废失败')
      } finally {
        this.loading = false
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        alert('已复制')
      } catch {
        alert('复制失败，请手动复制')
      }
    }
  }
}
</script>

<style scoped>
.page { padding: 20px; }
.hint { color: #666; margin: 0 0 16px; font-size: 14px; }
.toolbar { display: flex; gap: 8px; margin-bottom: 16px; align-items: center; }
.toolbar select, .toolbar button { padding: 6px 12px; }
.toolbar .primary { background: #1677ff; color: #fff; border: none; border-radius: 4px; cursor: pointer; }
table { width: 100%; border-collapse: collapse; font-size: 14px; }
th, td { border: 1px solid #eee; padding: 8px; text-align: left; }
button { margin-right: 6px; cursor: pointer; }
.dialog-mask { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.dialog { background: #fff; padding: 24px; border-radius: 8px; width: 480px; max-width: 90vw; }
.field-label { display: block; margin-bottom: 6px; font-size: 14px; }
.input { width: 100%; padding: 8px; box-sizing: border-box; margin-bottom: 16px; }
.actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.actions .primary { background: #1677ff; color: #fff; border: none; border-radius: 4px; padding: 6px 16px; }
.warn { color: #d48806; font-size: 13px; margin: 0 0 12px; }
.credential-box { background: #fafafa; border: 1px solid #eee; border-radius: 6px; padding: 12px; }
.credential-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.credential-label { min-width: 80px; color: #666; font-size: 13px; }
code { font-size: 16px; font-weight: 600; letter-spacing: 1px; }
</style>
